/**
 * Clean Video Service - Single source of truth for all video data
 * Eliminates duplicate APIs and provides consistent data across all components
 */
class VideoCatalogue {
    
    // 🔧 DEPENDENCY INJECTION: Accept http as parameter to avoid circular imports
    static setHttpClient(httpClient) {
        this.http = httpClient;
    }
    
    /**
     * Build query string from parameters
     * @param {Object} params - Query parameters
     * @returns {string} - Formatted query string
     */
    static buildQuery(params = {}) {
        const queryParams = [];
        
        if (params.userId && params.userId !== 'undefined') {
            queryParams.push(`userId=${encodeURIComponent(params.userId)}`);
        }
        
        if (params.pageSize) {
            queryParams.push(`PageSize=${encodeURIComponent(params.pageSize)}`);
        }
        
        if (params.pageNumber) {
            queryParams.push(`PageNumber=${encodeURIComponent(params.pageNumber)}`);
        }
        
        if (params.searchName) {
            queryParams.push(`searchName=${encodeURIComponent(params.searchName)}`);
        }
        
        if (params.categoryId && params.categoryId !== 0) {
            queryParams.push(`categoryId=${encodeURIComponent(params.categoryId)}`);
        }
        
        if (params.subCategoryId && params.subCategoryId !== 0) {
            queryParams.push(`subCategoryId=${encodeURIComponent(params.subCategoryId)}`);
        }
        
        if (params.classificationId && params.classificationId !== 0) {
            queryParams.push(`classificationId=${encodeURIComponent(params.classificationId)}`);
        }
        
        if (params.keywordIds && params.keywordIds.length > 0) {
            const keywordQuery = params.keywordIds
                .map(id => `keywordIds=${encodeURIComponent(id)}`)
                .join('&');
            queryParams.push(keywordQuery);
        }
        
        if (params.filterVideoId) {
            queryParams.push(`FilterVideoId=${encodeURIComponent(params.filterVideoId)}`);
        }
        
        return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    }
    
    /**
     * Get videos with simple list format
     * @param {Object} filters - Filter parameters
     * @returns {Promise} - API response with videos array
     */
    static async getVideosList(filters = {}) {
        try {
            const defaultFilters = {
                pageSize: 1000,
                pageNumber: 1,
                ...filters
            };
            
            const queryString = this.buildQuery(defaultFilters);
            const response = await this.http.get(`TrainingVideos${queryString}`);
            
            if (response?.isSuccess) {
                return {
                    ...response,
                    data: {
                        ...response.data,
                        // Ensure consistent structure
                        videos: response.data?.videos || []
                    }
                };
            }
            
            return response;
        } catch (error) {
            console.error('VideoCatalogue.getVideosList error:', error);
            return Promise.reject(error?.response?.data || error);
        }
    }
    
    /**
     * Get videos organized by categories and duration
     * @param {Object} filters - Filter parameters  
     * @returns {Promise} - API response with categoryWise and durationWise data
     */
    static async getVideosByCategory(filters = {}) {
        try {
            const defaultFilters = {
                pageSize: 1000,
                pageNumber: 1,
                ...filters
            };
            
            const queryString = this.buildQuery(defaultFilters);
            // 🔧 FIXED: Use single endpoint and process data in frontend
            const response = await this.http.get(`TrainingVideos${queryString}`);
            
            if (response?.isSuccess) {
                const videos = response.data?.videos || [];
                
                // 🔧 FRONTEND PROCESSING: Group videos by category (moved from backend)
                const categoryWise = {};
                videos.forEach(video => {
                    const categoryName = video.category?.name || 'Uncategorized';
                    if (!categoryWise[categoryName]) {
                        categoryWise[categoryName] = [];
                    }
                    categoryWise[categoryName].push(video);
                });
                
                // 🔧 FRONTEND PROCESSING: Group videos by duration (moved from backend)
                const durationWise = {
                    "Under 10 Sec": videos.filter(v => v.duration < 10),
                    "10 Sec - 45 Sec": videos.filter(v => v.duration >= 10 && v.duration < 45),
                    "45 Sec - 2 mins": videos.filter(v => v.duration >= 45 && v.duration < 120)
                };
                
                return {
                    ...response,
                    data: {
                        ...response.data,
                        categoryWise,
                        durationWise,
                        count: response.data?.count || 0
                    }
                };
            }
            
            return response;
        } catch (error) {
            console.error('VideoCatalogue.getVideosByCategory error:', error);
            return Promise.reject(error?.response?.data || error);
        }
    }
    
    /**
     * Get single video details
     * @param {string} videoId - Video ID
     * @param {string} userId - User ID (optional)
     * @returns {Promise} - API response with video details
     */
    static async getVideoDetail(videoId, userId = null) {
        try {
            const query = userId ? `?userId=${userId}` : "";
            const response = await this.http.get(`TrainingVideos/${videoId}${query}`);
            return response;
        } catch (error) {
            console.error('VideoCatalogue.getVideoDetail error:', error);
            return Promise.reject(error?.response?.data || error);
        }
    }
    
    /**
     * Get related videos for a specific category
     * @param {Object} params - Parameters including categoryId, userId, etc.
     * @returns {Promise} - API response with related videos
     */
    static async getRelatedVideos(params = {}) {
        try {
            const { categoryId, page, limit, excludeVideoId, userId } = params;
            
            const filters = {
                categoryId,
                pageNumber: page || 1,
                pageSize: limit || 10,
                filterVideoId: excludeVideoId,
                userId
            };
            
            return await this.getVideosList(filters);
        } catch (error) {
            console.error('VideoCatalogue.getRelatedVideos error:', error);
            return Promise.reject(error?.response?.data || error);
        }
    }
    
    /**
     * Process category-wise videos for Home component display
     * @param {Object} categoryData - Raw category data from API
     * @returns {Object} - Processed subcategory-wise videos
     */
    static processSubCategoryVideos(categoryData) {
        if (!categoryData?.TRAINING) {
            return {
                "Lower Body": {},
                "Upper Body": {},
                "Core Stability": {}
            };
        }
        
        const filteredVideos = categoryData.TRAINING;
        
        return {
            "Lower Body": filteredVideos
                .filter(video => video?.subCategory?.name === "Lower Body")
                .reduce((acc, video) => {
                    const classification = video?.classification?.name || 'Uncategorized';
                    if (!acc[classification]) {
                        acc[classification] = [];
                    }
                    acc[classification].push(video);
                    return acc;
                }, {}),
                
            "Upper Body": filteredVideos
                .filter(video => video?.subCategory?.name === "Upper Body")
                .reduce((acc, video) => {
                    const classification = video?.classification?.name || 'Uncategorized';
                    if (!acc[classification]) {
                        acc[classification] = [];
                    }
                    acc[classification].push(video);
                    return acc;
                }, {}),
                
            "Core Stability": filteredVideos
                .filter(video => video?.subCategory?.name === "Core Stability")
                .reduce((acc, video) => {
                    const classification = video?.classification?.name || 'Uncategorized';
                    if (!acc[classification]) {
                        acc[classification] = [];
                    }
                    acc[classification].push(video);
                    return acc;
                }, {})
        };
    }
    
    /**
     * Extract bookmark IDs from video list
     * @param {Array} videos - Array of video objects
     * @returns {Array} - Array of bookmarked video IDs
     */
    static extractBookmarkIds(videos = []) {
        return videos
            .filter(video => video?.isBookMarked === true)
            .map(video => video?.id)
            .filter(id => id); // Remove any null/undefined IDs
    }
    
    /**
     * Fetch bookmarked video IDs from dedicated API
     * @param {string} userId - User ID
     * @returns {Promise<Array>} - Array of bookmarked video IDs
     */
    static async fetchBookmarkedIds(userId) {
        try {
            if (!userId || userId === 'undefined') {
                return [];
            }
            
            const response = await this.http.get(`User/Bookmark?userId=${userId}&PageNumber=1&PageSize=1000`);
            
            if (response?.isSuccess) {
                const bookmarkedIds = response.data?.map(item => item.videoId || item.id) || [];
                console.log('✅ VideoCatalogue: Fetched bookmarked IDs:', bookmarkedIds);
                return bookmarkedIds;
            }
            
            return [];
        } catch (error) {
            console.error('❌ VideoCatalogue: Failed to fetch bookmarked IDs:', error);
            return [];
        }
    }
}

export default VideoCatalogue;
